import React from "react";
import { Helmet } from "react-helmet-async";

// Meta descriptions específicas por tipo de página
const META_DESCRIPTIONS = {
  home: "Trineo conecta usuarios con transportistas para fletes y traslados en Santiago de forma rápida y segura. Disponible vía app móvil y WhatsApp.",
  app: "Descarga la app de Trineo y solicita fletes al instante. Conectamos usuarios con transportistas verificados para traslados seguros y eficientes.",
  nosotros:
    "Conoce Trineo, la plataforma líder en Chile para servicios de flete y transporte. Conectamos usuarios con transportistas confiables desde 2023.",
  faq: "Resuelve todas tus dudas sobre Trineo. Preguntas frecuentes sobre fletes, transportistas, precios y cómo usar nuestra plataforma.",
  contacto:
    "Contáctanos para soporte, consultas comerciales o información sobre servicios de flete. Trineo está aquí para ayudarte.",
  transportista:
    "Únete como transportista a Trineo y genera ingresos adicionales. Conecta con usuarios que necesitan servicios de flete y transporte.",
  soporte:
    "Centro de soporte de Trineo. Encuentra ayuda técnica, reporta problemas y obtén asistencia para usar nuestra plataforma.",
  terminos:
    "Términos y condiciones de uso de Trineo. Conoce las reglas y políticas para usar nuestra plataforma de fletes y transporte.",
  privacidad:
    "Política de privacidad de Trineo. Conoce cómo protegemos y manejamos tus datos personales en nuestra plataforma.",
  cookies:
    "Política de cookies de Trineo. Información sobre el uso de cookies en nuestro sitio web y cómo configurar tus preferencias.",
  marketing:
    "Trineo para empresas. Soluciones logísticas corporativas y servicios de flete para negocios. Optimiza tu cadena de suministro.",
  local: {
    santiago:
      "Servicios de flete en Santiago - Trineo. Transportistas verificados para mudanzas, traslados y fletes en la Región Metropolitana.",
    valparaiso:
      "Fletes en Valparaíso - Trineo. Conecta con transportistas locales para mudanzas y traslados en la Quinta Región.",
    concepcion:
      "Servicios de transporte en Concepción - Trineo. Fletes y mudanzas con transportistas verificados en la Región del Biobío.",
  },
  comparison:
    "Trineo vs competencia. Compara características, precios y beneficios de nuestra plataforma de fletes frente a otras opciones del mercado.",
  workflow:
    "Descubre el proceso simple de Trineo para solicitar fletes: solicita por app o WhatsApp, elige transportista y recibe tu pedido de forma segura.",
};

// Títulos específicos por tipo de página
const PAGE_TITLES = {
  home: "Trineo - Fletes y Transporte en Santiago",
  app: "Descargar App Trineo - Fletes al Instante",
  nosotros: "Sobre Trineo - Líderes en Servicios de Flete",
  faq: "Preguntas Frecuentes - Trineo",
  contacto: "Contacto - Trineo Soporte y Ventas",
  transportista: "Únete como Transportista - Trineo",
  soporte: "Centro de Soporte - Trineo",
  terminos: "Términos y Condiciones - Trineo",
  privacidad: "Política de Privacidad - Trineo",
  cookies: "Política de Cookies - Trineo",
  marketing: "Trineo para Empresas - Soluciones Logísticas",
  local: {
    santiago: "Fletes en Santiago - Trineo",
    valparaiso: "Fletes en Valparaíso - Trineo",
    concepcion: "Fletes en Concepción - Trineo",
  },
  comparison: "Trineo vs Competencia - Comparación de Servicios",
  workflow: "Cómo Funciona Trineo - Proceso Simple y Seguro",
};

// Keywords específicas por tipo de página
const PAGE_KEYWORDS = {
  home: "fletes santiago, transporte santiago, app fletes, mudanzas santiago, traslados, transportistas",
  app: "app trineo, descargar app fletes, aplicación transporte, app mudanzas",
  nosotros: "trineo empresa, historia trineo, equipo trineo, misión visión",
  faq: "preguntas trineo, ayuda fletes, soporte transporte, dudas trineo",
  contacto: "contacto trineo, soporte cliente, atención cliente, ayuda trineo",
  transportista:
    "trabajar transportista, ganar dinero transporte, conductor trineo, partner trineo",
  soporte: "soporte técnico, ayuda usuario, centro ayuda, asistencia trineo",
  terminos: "términos uso, condiciones servicio, legal trineo, políticas",
  privacidad:
    "privacidad datos, protección información, gdpr, datos personales",
  cookies: "cookies web, preferencias navegador, tracking, analytics",
  marketing:
    "trineo empresas, logística corporativa, fletes empresariales, b2b",
  local: {
    santiago:
      "fletes santiago, transportistas santiago, mudanzas rm, traslados metropolitana",
    valparaiso:
      "fletes valparaiso, transportistas quinta región, mudanzas valpo",
    concepcion: "fletes concepción, transportistas biobío, mudanzas concepción",
  },
  comparison:
    "trineo vs uber, comparar apps flete, mejor app transporte, ventajas trineo",
};

/**
 * Componente SEO para optimizar el SEO on-page
 * @param {Object} props - Propiedades del componente
 * @param {string} props.pageType - Tipo de página (home, app, nosotros, etc.)
 * @param {string} props.localArea - Área local para páginas locales (santiago, valparaiso, concepcion)
 * @param {string} props.title - Título personalizado (opcional)
 * @param {string} props.description - Descripción personalizada (opcional)
 * @param {string} props.keywords - Palabras clave personalizadas (opcional)
 * @param {string} props.canonicalUrl - URL canónica de la página
 * @param {string} props.ogImage - URL de la imagen para Open Graph
 * @param {string} props.ogType - Tipo de contenido para Open Graph
 * @param {Object} props.schema - Datos estructurados Schema.org en formato JSON
 */
const SEO = ({
  pageType = "home",
  localArea = null,
  title = null,
  description = null,
  keywords = null,
  canonicalUrl = "https://www.trineoapp.com",
  ogImage = "https://www.trineoapp.com/images/og-image.jpg",
  ogType = "website",
  schema = null,
}) => {
  // Obtener meta description según el tipo de página
  const getMetaDescription = () => {
    if (description) return description;

    if (pageType === "local" && localArea) {
      return META_DESCRIPTIONS.local[localArea] || META_DESCRIPTIONS.home;
    }

    return META_DESCRIPTIONS[pageType] || META_DESCRIPTIONS.home;
  };

  // Obtener título según el tipo de página
  const getPageTitle = () => {
    if (title) return title;

    if (pageType === "local" && localArea) {
      return PAGE_TITLES.local[localArea] || PAGE_TITLES.home;
    }

    return PAGE_TITLES[pageType] || PAGE_TITLES.home;
  };

  // Obtener keywords según el tipo de página
  const getPageKeywords = () => {
    if (keywords) return keywords;

    if (pageType === "local" && localArea) {
      return PAGE_KEYWORDS.local[localArea] || PAGE_KEYWORDS.home;
    }

    return PAGE_KEYWORDS[pageType] || PAGE_KEYWORDS.home;
  };

  const finalTitle = getPageTitle();
  const finalDescription = getMetaDescription();
  const finalKeywords = getPageKeywords();
  // Schema.org por defecto para la organización
  const defaultSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Trineo",
    url: "https://www.trineoapp.com",
    logo: "https://www.trineoapp.com/logo.png",
    sameAs: [
      "https://www.facebook.com/TrineoApp",
      "https://www.instagram.com/trineoapp/",
    ],
    description: finalDescription,
  };

  // Usar el schema proporcionado o el predeterminado
  const schemaData = schema || defaultSchema;

  return (
    <Helmet>
      {/* Metadatos básicos */}
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalKeywords} />

      {/* Metadatos para dispositivos móviles */}
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1, maximum-scale=5"
      />
      <meta name="theme-color" content="#1976D2" />

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={ogImage} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={ogImage} />

      {/* Schema.org JSON-LD */}
      <script type="application/ld+json">{JSON.stringify(schemaData)}</script>

      {/* Preconectar a dominios externos importantes */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link
        rel="preconnect"
        href="https://fonts.gstatic.com"
        crossOrigin="anonymous"
      />
      <link rel="preconnect" href="https://www.google-analytics.com" />
    </Helmet>
  );
};

export default SEO;
